import { EmailVerificationStatus } from '@rover/types/src/EmailVerification';
import inject from '@rover/utilities/injector';
import renderRootElement from '@rover/utilities/renderRootElement';

import type { Props } from './EmailVerificationPage';
import EmailVerificationPage from './EmailVerificationPage';

const BLOCKING_MODAL_SELECTOR = '#react-email-verify-unified-page';

type MountOptions = {
  /**
   * Closes the modal. The dismissal contract belongs to the legacy modal machinery,
   * so the caller supplies it rather than this package reaching for jQuery.
   */
  onAcknowledge: () => void;
};

/**
 * Renders the shared verification page into the Django blocking modal.
 *
 * The modal arrives as an HTML fragment fetched after the bundle has loaded, so the
 * mount runs on demand rather than at import time, and the page's context comes from
 * the script block in `email-verification-blocking-modal.html`.
 *
 * The mount point only exists when the `rollout_verify_email_ssr` gate is on, so its
 * absence is how the caller learns to fall back to the legacy modal. Returns whether
 * the page was mounted.
 */
export const mountEmailVerificationBlockingModal = ({ onAcknowledge }: MountOptions): boolean => {
  if (!process.env.JS_ENV_CLIENT) {
    return false;
  }

  if (!document.querySelector(BLOCKING_MODAL_SELECTOR)) {
    return false;
  }

  const props: Props = {
    // The modal blocks the site outright, so it is compulsory whatever the account
    // status says: skipping is not on offer, only verifying or logging out.
    flow: EmailVerificationStatus.COMPULSORY,
    source: inject('Rover.context.previousUrl', ''),
    // Unlike the standalone page, the modal's view always resolves a `next_url`, and
    // the logout has to honour it.
    nextUrl: inject('Rover.context.nextUrl', ''),
    onExit: onAcknowledge,
    isEmbedded: true,
  };

  renderRootElement(EmailVerificationPage, BLOCKING_MODAL_SELECTOR, props);
  return true;
};

export default mountEmailVerificationBlockingModal;
